import {
  Box,
  Check,
  CheckCircle,
  Clock,
  Container,
  Cpu,
  ExternalLink,
  GitBranch,
  GitCommit,
  Info,
  Layers,
  Loader2,
  Plug,
  RefreshCw,
  Rocket,
  Search,
  Server,
  Tag,
  Terminal,
  XCircle,
} from "lucide-react";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ErrorState } from "../components/common/ErrorState";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { CopyButton } from "../components/ui/copy-button";
import { Input } from "../components/ui/input";
import { PageHeader } from "../components/ui/page-header";
import { Progress } from "../components/ui/progress";
import { api } from "../lib/api";
import { createLogger } from "../lib/logger";
import { Deployment, PipelineLog } from "../types";

// Scoped logger (replaces console.*) — see docs/logging.md.
const log = createLogger("DeploymentDetailsPage");

// The ordered CI/CD pipeline the backend reports via `currentStage`.
const PIPELINE_STAGES = ["prepare", "build", "configure", "deploy", "verify"];

// mm:ss (or h:mm:ss for long runs).
const formatDuration = (totalSeconds: number) => {
  if (!isFinite(totalSeconds) || totalSeconds < 0) return "—";
  const s = Math.floor(totalSeconds % 60);
  const m = Math.floor((totalSeconds / 60) % 60);
  const h = Math.floor(totalSeconds / 3600);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

// "just now" / "2m ago" / "3h ago" relative to `nowMs`.
const formatRelative = (dateStr: string | undefined, nowMs: number) => {
  if (!dateStr) return "—";
  const then = new Date(dateStr).getTime();
  if (isNaN(then)) return "—";
  const diff = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (diff < 10) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const DeploymentDetailsPage: React.FC = () => {
  const { projectId, deploymentId } = useParams<{
    projectId: string;
    deploymentId: string;
  }>();
  const navigate = useNavigate();
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [logFilter, setLogFilter] = useState<"all" | "error" | "warn">("all");
  const [logSearch, setLogSearch] = useState("");
  // The breadcrumb showed a hardcoded "Project" crumb — the deployment payload
  // carries `serviceName` but no project name, so fetch it the same way
  // ServiceDetailsPage does. A crumb that can't name where it leads is just a
  // back button wearing a breadcrumb's clothes.
  const [projectName, setProjectName] = useState<string>("");
  const logBodyRef = useRef<HTMLDivElement>(null);
  // SSE state — tracks the live log stream independently from the REST snapshot.
  // WHY separate state: EventSource pushes entries one by one; merging into `deployment` // would trigger a full object re-render on every log line.
  const [sseLogs, setSseLogs] = useState<PipelineLog[]>([]);
  const [sseStatus, setSseStatus] = useState<
    "idle" | "connecting" | "live" | "closed"
  >("idle");
  // Overrides deployment.status once SSE reports a terminal state — REST may lag.
  const [sseDeployStatus, setSseDeployStatus] = useState<string | null>(null);
  // Fetch deployment details
  const fetchDeployment = useCallback(async () => {
    if (!deploymentId) return;
    try {
      const response = await api.deployments.getById(deploymentId);
      setDeployment(response.deployment);
      setError(null);
      // Stop polling if deployment is complete or failed
      const finalStatuses = ["running", "failed", "completed"];
      if (finalStatuses.includes(response.deployment.status)) {
        setIsPolling(false);
      }
    } catch (err: any) {
      log.error("Error fetching deployment", err);
      setError(err.message || "Failed to fetch deployment details");
    } finally {
      setLoading(false);
    }
  }, [deploymentId]);
  // Initial fetch
  useEffect(() => {
    fetchDeployment();
  }, [fetchDeployment]);
  // Project name for the breadcrumb. Non-blocking: the crumb falls back to
  // "Project" so the trail still works if this fails.
  useEffect(() => {
    if (!projectId) return;
    api.projects
      .get(projectId)
      .then((data) => setProjectName(data.project?.name ?? ""))
      .catch(() => {
        /* breadcrumb keeps its fallback label */
      });
  }, [projectId]);
  // Start polling when deployment is in progress.
  // WHY skip when SSE active: SSE delivers log entries in real time, making REST
  // polling redundant and potentially confusing (two sources of truth).
  useEffect(() => {
    if (!deployment) return;
    if (sseStatus === "live" || sseStatus === "connecting") return;
    const inProgressStatuses = ["pending", "queued", "building", "deploying"];
    if (inProgressStatuses.includes(deployment.status)) {
      setIsPolling(true);
    }
  }, [deployment, sseStatus]);
  // Poll every 3 seconds while deployment is in progress
  useEffect(() => {
    if (!isPolling) return;
    const interval = setInterval(() => {
      fetchDeployment();
    }, 3000);
    return () => clearInterval(interval);
  }, [isPolling, fetchDeployment]);
  // Tick once a second so the live elapsed-time readout updates in progress.
  useEffect(() => {
    if (!isPolling && sseStatus !== "live" && sseStatus !== "connecting")
      return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isPolling, sseStatus]);
  // Open SSE stream for in-flight deployments. Replaces 3-second REST polling as the
  // primary log delivery mechanism.
  // WHY open here and not inside fetchDeployment: deployment.serviceId is only available
  // after the initial REST fetch completes; this effect fires once it appears.
  useEffect(() => {
    if (!deployment?.serviceId || !deploymentId) return;
    const transientStatuses = [
      "queued",
      "pending",
      "cloning",
      "building",
      "deploying",
    ];
    // If the REST snapshot already shows a terminal state, logs are complete — skip SSE.
    if (!transientStatuses.includes(deployment.status)) return;
    setSseStatus("connecting");
    const es = api.deployments.streamLogs(deployment.serviceId, deploymentId);
    es.addEventListener("log", (e: MessageEvent) => {
      setSseStatus("live");
      setSseLogs((prev) => [...prev, JSON.parse(e.data) as PipelineLog]);
    });
    es.addEventListener("status", (e: MessageEvent) => {
      const { status: s } = JSON.parse(e.data);
      setSseDeployStatus(s);
    });
    es.addEventListener("done", (e: MessageEvent) => {
      const { status: s } = JSON.parse(e.data);
      setSseDeployStatus(s);
      setSseStatus("closed");
      es.close();
      // Stop REST polling — SSE just delivered the final state.
      setIsPolling(false);
    });
    es.onerror = () => {
      // EventSource auto-reconnects on transient network errors; stay "connecting" so
      // the UI shows reconnecting state rather than falsely showing "closed".
      setSseStatus("connecting");
    };
    return () => es.close();
    // Only re-open when the deployment identity changes, not on every status update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployment?.serviceId, deploymentId]);
  // Prefer SSE log entries when the stream is active; fall back to the REST snapshot.
  // WHY: the SSE handler streams all existing entries first, so sseLogs is a superset
  // of pipelineLogs — no merging needed, just a priority switch.
  const displayLogs =
    sseLogs.length > 0 ? sseLogs : (deployment?.pipelineLogs ?? []);
  // Filter logs by level + free-text search.
  const filteredLogs = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    return displayLogs.filter((l) => {
      const levelOk = logFilter === "all" || l.level === logFilter;
      const textOk =
        !q ||
        l.message.toLowerCase().includes(q) ||
        l.stage.toLowerCase().includes(q) ||
        (l.details?.toLowerCase().includes(q) ?? false);
      return levelOk && textOk;
    });
  }, [displayLogs, logFilter, logSearch]);
  // Logs are shown newest-first, so scroll to the top to reveal the latest
  // line while streaming (either via SSE or REST polling).
  const logCount = filteredLogs.length;
  useEffect(() => {
    if ((isPolling || sseStatus === "live") && logBodyRef.current) {
      logBodyRef.current.scrollTop = 0;
    }
  }, [logCount, isPolling, sseStatus]);
  // Get status badge variant
  const getStatusBadge = (status: string) => {
    const variants: Record<
      string,
      {
        variant: "default" | "secondary" | "destructive" | "outline";
        icon: React.ReactNode;
      }
    > = {
      pending: {
        variant: "secondary",
        icon: <Clock className="w-3 h-3 mr-1" />,
      },
      queued: {
        variant: "secondary",
        icon: <Clock className="w-3 h-3 mr-1" />,
      },
      building: {
        variant: "default",
        icon: <Loader2 className="w-3 h-3 mr-1 animate-spin" />,
      },
      deploying: {
        variant: "default",
        icon: <Loader2 className="w-3 h-3 mr-1 animate-spin" />,
      },
      running: {
        variant: "outline",
        icon: <CheckCircle className="w-3 h-3 mr-1 text-success" />,
      },
      completed: {
        variant: "outline",
        icon: <CheckCircle className="w-3 h-3 mr-1 text-success" />,
      },
      failed: {
        variant: "destructive",
        icon: <XCircle className="w-3 h-3 mr-1" />,
      },
    };
    const config = variants[status] || variants.pending;
    return (
      <Badge variant={config.variant} className="flex items-center">
        {config.icon}
        {status}
      </Badge>
    );
  };
  // A small status dot is the only color in the log stream, so errors/warnings
  // stand out instead of every line being tinted.
  const getLevelDot = (level: string) => {
    const dot: Record<string, string> = {
      error: "bg-destructive",
      warn: "bg-warning",
      success: "bg-success",
      info: "bg-info",
    };
    return dot[level] || "bg-muted-foreground";
  };
  // Only error/warning rows get a tinted background + accent; the rest stay
  // neutral and readable.
  const getLevelRow = (level: string) => {
    const row: Record<string, string> = {
      error: "border-l-2 border-destructive bg-danger-bg/50",
      warn: "border-l-2 border-warning bg-warning/5",
    };
    return row[level] || "border-l-2 border-transparent";
  };
  // Neutral, monochrome stage chip (theme-aware) shared by the info card and
  // the log rows.
  const stageChip = "border border-border bg-muted/60 text-muted-foreground";
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }
  if (error || !deployment) {
    return (
      <ErrorState
        title="Error Loading Deployment"
        message={
          error ||
          "Deployment not found. It may have been deleted or you don't have access to it."
        }
        action={{
          label: "Try Again",
          onClick: fetchDeployment,
        }}
        secondaryAction={{
          label: "Back to Project",
          onClick: () => navigate(`/projects/${projectId}`),
          variant: "outline",
        }}
      />
    );
  }
  // WHY override with sseDeployStatus: SSE reports the final status via the "done" event,
  // often before the REST endpoint has been updated. This keeps the UI accurate.
  const status = sseDeployStatus ?? deployment.status;
  // "running" is this platform's success state; tolerate a "completed" value too.
  const isDone = status === "running" || (status as string) === "completed";
  const isFailed = status === "failed";
  const currentIdx = Math.max(
    0,
    PIPELINE_STAGES.indexOf(
      (deployment.currentStage || "prepare").toLowerCase()
    )
  );
  const progress = isDone
    ? 100
    : isFailed
      ? Math.round((currentIdx / PIPELINE_STAGES.length) * 100)
      : Math.round(((currentIdx + 0.5) / PIPELINE_STAGES.length) * 100);
  const startMs = new Date(
    deployment.startedAt || deployment.createdAt
  ).getTime();
  const endMs = deployment.finishedAt
    ? new Date(deployment.finishedAt).getTime()
    : now;
  const elapsedSeconds = (endMs - startMs) / 1000;
  const deployUrl = deployment.deployURL || deployment.url;
  // Managed services (databases like Mongo/Qdrant) are provisioned from images,
  // not built from a git repo — so a source branch/commit is meaningless. Only
  // show the Source panel for git-built services that actually have provenance.
  const isManagedService = (deployment.serviceType || "").includes("database");
  const showSource =
    !isManagedService && Boolean(deployment.repoUrl || deployment.commitHash);
  const stageState = (
    i: number
  ): "complete" | "active" | "failed" | "pending" => {
    if (isDone) return "complete";
    if (isFailed)
      return i < currentIdx
        ? "complete"
        : i === currentIdx
          ? "failed"
          : "pending";
    return i < currentIdx
      ? "complete"
      : i === currentIdx
        ? "active"
        : "pending";
  };
  const stageNodeClass: Record<string, string> = {
    complete: "border-success bg-success text-success-foreground",
    active: "border-primary bg-primary/10 text-primary",
    failed: "border-destructive bg-destructive text-destructive-foreground",
    pending: "border-border bg-muted text-muted-foreground",
  };
  const allLogsText = displayLogs
    .map(
      (l) =>
        `[${new Date(l.timestamp).toLocaleTimeString()}] ${l.stage} ${l.level}: ${l.message}${
          l.details ? ` — ${l.details}` : ""
        }`
    )
    .join("\n");
  return (
    <div className="space-y-6">
      {/* Header — the breadcrumb is passed in so it sits inside the header's
          own rhythm rather than floating above it with its own spacing. */}
      <PageHeader
        breadcrumb={
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/projects">Projects</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={`/projects/${projectId}`}>
                    {projectName || "Project"}
                  </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {deployment.serviceId && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to={`/projects/${projectId}/services/${deployment.serviceId}`}
                  >
                    {deployment.serviceName || deployment.name || "Service"}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Deployment</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
        }
        icon={Rocket}
        title={deployment.serviceName || deployment.name}
        meta={
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
            <span>Deployment</span>
            <span aria-hidden="true">·</span>
            <span className="font-mono text-xs">{deployment.id}</span>
            <CopyButton
              value={deployment.id}
              label="Copy deployment ID"
              size="icon"
              className="h-5 w-5"
            />
          </div>
        }
        actions={
        <>
          {getStatusBadge(status)}
          {deployUrl && (
            <Button
              variant="default"
              size="sm"
              onClick={() => window.open(deployUrl, "_blank", "noopener")}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Open
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDeployment}
            disabled={loading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </>
        }
      />
      {/* Pipeline timeline */}
      <div className="rounded-lg bg-card p-5 ">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-h4">Pipeline</h2>
          <span className="text-xs tabular-nums text-muted-foreground">
            {progress}%
          </span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {PIPELINE_STAGES.map((stage, i) => {
            const st = stageState(i);
            return (
              <React.Fragment key={stage}>
                {i > 0 && (
                  <div
                    className={`h-0.5 min-w-6 flex-1 rounded ${
                      isDone || i <= currentIdx ? "bg-primary" : "bg-border"
                    }`}
                  />
                )}
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${stageNodeClass[st]}`}
                  >
                    {st === "complete" ? (
                      <Check className="h-4 w-4" />
                    ) : st === "active" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : st === "failed" ? (
                      <XCircle className="h-4 w-4" />
                    ) : (
                      <span className="text-xs font-medium">{i + 1}</span>
                    )}
                  </div>
                  <span
                    className={`text-xs capitalize ${
                      st === "pending"
                        ? "text-muted-foreground"
                        : "font-medium text-foreground"
                    }`}
                  >
                    {stage}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <Progress value={progress} className="mt-4" />
        {/* Timing */}
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3 border-t pt-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Started
            </p>
            <p
              className="font-medium"
              title={new Date(
                deployment.startedAt || deployment.createdAt
              ).toLocaleString()}
            >
              {formatRelative(
                deployment.startedAt || deployment.createdAt,
                now
              )}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {isDone || isFailed ? "Duration" : "Elapsed"}
            </p>
            <p className="flex items-center gap-1.5 font-medium tabular-nums">
              {formatDuration(elapsedSeconds)}
              {isPolling && (
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              )}
            </p>
          </div>
          {deployment.finishedAt && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Finished
              </p>
              <p
                className="font-medium"
                title={new Date(deployment.finishedAt).toLocaleString()}
              >
                {formatRelative(deployment.finishedAt, now)}
              </p>
            </div>
          )}
          {deployment.buildDuration ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Build
              </p>
              <p className="font-medium tabular-nums">
                {formatDuration(deployment.buildDuration)}
              </p>
            </div>
          ) : null}
        </div>
      </div>
      {/* Source / commit — git-built services only; hidden for managed
 databases (Mongo, Qdrant, …) that have no repo. */}
      {showSource && (
        <div className="rounded-lg bg-card p-4 ">
          <div className="mb-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <GitCommit className="h-3.5 w-3.5" />
            Source
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{deployment.branch || "main"}</span>
            </span>
            {deployment.commitHash && (
              <span className="inline-flex items-center gap-1">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                  {deployment.commitHash.slice(0, 7)}
                </code>
                <CopyButton
                  value={deployment.commitHash}
                  label="Copy commit hash"
                  size="icon"
                  className="h-6 w-6"
                />
              </span>
            )}
            {deployment.repoUrl && (
              <a
                href={deployment.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Repository
              </a>
            )}
          </div>
          {deployment.commitMessage && (
            <p
              className="mt-2 truncate text-sm text-muted-foreground"
              title={deployment.commitMessage}
            >
              {deployment.commitMessage}
            </p>
          )}
        </div>
      )}
      {/* Runtime details */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {(
          [
            {
              icon: Box,
              label: "Service Type",
              value: deployment.serviceType || deployment.framework || "N/A",
            },
            {
              icon: Server,
              label: "Environment",
              value: deployment.environment || "N/A",
            },
            { icon: Plug, label: "Port", value: deployment.port || "N/A" },
            { icon: Cpu, label: "Node", value: deployment.nodeVersion },
            { icon: Layers, label: "Replicas", value: deployment.replicas },
            {
              icon: Container,
              label: "Image",
              value: deployment.dockerImage,
            },
            {
              icon: Tag,
              label: "Version",
              // Git services already show the commit under Source.
              value: showSource ? undefined : deployment.version,
            },
          ] as const
        )
          .filter(
            (c) => c.value !== undefined && c.value !== null && c.value !== ""
          )
          .map(({ icon: Icon, label, value }) => (
            <div
              key={label}
              className="rounded-lg bg-card p-4  transition-colors hover:border-foreground/20"
            >
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />
                {label}
              </div>
              <p className="truncate font-semibold" title={String(value)}>
                {value}
              </p>
            </div>
          ))}
      </div>
      {/* Status Message (the failure banner below already surfaces it on error) */}
      {deployment.statusMessage && deployment.status !== "failed" && (
        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertDescription>{deployment.statusMessage}</AlertDescription>
        </Alert>
      )}
      {/* Pipeline Logs */}
      <div className="overflow-hidden rounded-lg bg-card ">
        {/* Console-style header bar */}
        <div className="flex items-center justify-between border-b bg-muted/50 px-4 py-2.5">
          <div className="flex items-center gap-3">
            <div className="hidden gap-1.5 sm:flex" aria-hidden="true">
              <span className="h-3 w-3 rounded-full bg-destructive/70" />
              <span className="h-3 w-3 rounded-full bg-warning/70" />
              <span className="h-3 w-3 rounded-full bg-success/70" />
            </div>
            <span className="flex items-center gap-2 text-sm font-semibold">
              <Terminal className="h-4 w-4 text-muted-foreground" />
              Deployment Logs
            </span>
            {displayLogs.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {displayLogs.length} entries
              </span>
            )}
          </div>
          {/* WHY three states: SSE "live" = actively receiving; "connecting" = reconnecting
 after error; neither = fall back to polling indicator. */}
          {sseStatus === "live" && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-2 w-2 animate-pulse rounded-full bg-success" />
              Live
            </div>
          )}
          {sseStatus === "connecting" && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Connecting…
            </div>
          )}
          {sseStatus === "idle" && isPolling && (
            <div className="flex items-center text-xs text-muted-foreground">
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              Auto-refreshing
            </div>
          )}
        </div>
        {/* Controls */}
        {displayLogs.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-1">
              {(["all", "error", "warn"] as const).map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={logFilter === f ? "secondary" : "ghost"}
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setLogFilter(f)}
                >
                  {f === "all" ? "All" : f === "error" ? "Errors" : "Warnings"}
                </Button>
              ))}
            </div>
            <div className="relative ml-auto flex items-center">
              <Search className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
                placeholder="Search logs…"
                className="h-7 w-44 pl-7 text-xs"
              />
            </div>
            <CopyButton
              value={allLogsText}
              label="Copy all logs"
              size="icon"
              className="h-7 w-7"
            />
          </div>
        )}
        {/* Log body */}
        <div
          ref={logBodyRef}
          className="max-h-[600px] overflow-auto p-2 font-mono text-sm"
        >
          {filteredLogs.length > 0 ? (
            <div className="divide-y divide-border/40">
              {[...filteredLogs]
                .reverse()
                .map((entry: PipelineLog, index: number) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 px-3 py-2 transition-colors hover:bg-muted/40 ${getLevelRow(
                      entry.level
                    )}`}
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${getLevelDot(
                        entry.level
                      )}`}
                      aria-hidden="true"
                    />
                    <span className="shrink-0 whitespace-nowrap pt-0.5 text-xs tabular-nums text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </span>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-2xs font-medium uppercase tracking-wide ${stageChip}`}
                    >
                      {entry.stage}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="text-foreground">{entry.message}</span>
                      {entry.details && (
                        <span className="mt-0.5 block whitespace-pre-wrap break-words text-xs text-muted-foreground">
                          {entry.details}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
            </div>
          ) : displayLogs.length > 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="mx-auto mb-3 h-10 w-10 opacity-40" />
              <p className="text-sm">No logs match your filter.</p>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Clock className="mx-auto mb-3 h-10 w-10 opacity-40" />
              <p className="text-sm">No logs yet. Deployment is starting…</p>
            </div>
          )}
        </div>
      </div>
      {/* Completion Message */}
      {status === "running" && (
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Deployment Successful!</AlertTitle>
          <AlertDescription>
            Your service is now running and ready to accept traffic.
            {(deployment.deployURL || deployment.url) && (
              <a
                href={deployment.deployURL || deployment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 block w-fit font-medium underline underline-offset-2"
              >
                {deployment.deployURL || deployment.url}
              </a>
            )}
          </AlertDescription>
        </Alert>
      )}
      {status === "failed" && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Deployment Failed</AlertTitle>
          <AlertDescription>
            {deployment.statusMessage ||
              "Check the logs above for error details."}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default DeploymentDetailsPage;
